import React, { FC, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import { useHistory } from 'react-router-dom';
import { Avatar, Button, Card, Divider, message, Modal, Space, Tooltip } from 'antd';
import {
  CloudUploadOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from '@ant-design/icons';

import EnvironmentSelect from '@lib/components/env-select';
import PagingAgGrid, { PagingAgGridRef } from '@lib/components/paging-grid/paging-grid';
import TaskState from '@lib/components/task/task-state';
import { ColDef } from '@lib/components/ui/ag-grid';
import { CsInboxV3Location } from '@lib/routes/routes';
import { PagingOptions } from '@lib/services/base/paginate';
import { csInboxService } from '@lib/services/cstool';
import { InboxHistory, UserRole } from '@lib/services/models';
import { useAuthorized } from '@lib/utils/authorize';
import dayjs, { DATE_TIME_FORMAT } from '@lib/utils/dayjs';
import { useQuery } from '@lib/utils/use-query';

const colDefs: ColDef[] = [
  {
    headerName: '#',
    field: 'id',
    minWidth: 60,
    maxWidth: 60,
    sort: 'desc',
    pinned: 'left',
  },
  {
    headerName: 'User',
    field: 'userEmail',
    cellRendererFramework: ({ value, data }: { value?: string; data: InboxHistory }) => (
      <span>
        <Avatar src={data.userAvatar} alt={value} size="small" /> {value}
      </span>
    ),
    minWidth: 160,
    maxWidth: 200,
  },
  {
    headerName: 'Version',
    field: 'version',
    minWidth: 90,
    maxWidth: 90,
  },
  {
    headerName: 'Environment',
    field: 'environment',
    minWidth: 150,
    maxWidth: 150,
  },
  {
    headerName: 'client Version',
    field: 'blueprintVersion',
    minWidth: 150,
    maxWidth: 150,
  },
  {
    headerName: 'Player Ids',
    field: 'playerIds',
    minWidth: 200,
    resizable: true,
    valueFormatter: ({ value }: any) => (value ? value.toString() : '-'),
  },
  {
    headerName: 'Note',
    field: 'note',
    minWidth: 200,
    resizable: true,
  },

  {
    headerName: 'Message',
    field: 'data',
    minWidth: 200,
    resizable: true,
    valueGetter: ({ data }: any) => {
      return data?.data ? JSON.stringify(data.data) : '-';
    },
  },
  {
    headerName: 'Register Time',
    field: 'createdAt',
    cellRendererFramework: ({ value }: any) => (
      <Tooltip title={dayjs(value).format(DATE_TIME_FORMAT)}>{dayjs(value).fromNow()}</Tooltip>
    ),
    minWidth: 150,
    maxWidth: 150,
    // sort: 'desc',
  },
  {
    headerName: 'Start Time',
    field: 'startTime',
    cellRendererFramework: ({ value }: any) =>
      value ? (
        <Tooltip title={dayjs(value).format(DATE_TIME_FORMAT)}>{dayjs(value).fromNow()}</Tooltip>
      ) : (
        '-'
      ),
    minWidth: 150,
    maxWidth: 150,
  },
  {
    headerName: 'Duration',
    field: 'endTime',
    valueFormatter: ({ data, value }: any) =>
      value
        ? dayjs
            .duration(dayjs(value).diff(dayjs(data.startTime), 'second'), 'second')
            .humanize(false)
        : '-',
    minWidth: 120,
    maxWidth: 120,
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 120,
    maxWidth: 120,
    cellRendererFramework: TaskState,
    pinned: 'right',
  },
];

const InboxHistoryList: FC = () => {
  const history = useHistory();
  const query = useQuery();

  const authorized = useAuthorized(UserRole.CsEditor);

  const gridRef = useRef<PagingAgGridRef>(null);
  const reloadTimerRef = useRef<NodeJS.Timeout>();

  const [environment, setEnvironment] = useState<string>();

  const pagingOptionsRef = useRef<PagingOptions<InboxHistory>['where']>({});

  const updateQuery = () => {
    const queries = [environment && `environment=${environment}`].filter(Boolean).join('&');
    history.push({
      pathname: history.location.pathname,
      search: `?${queries}`,
    });
  };

  const scheduleNextReload = () => {
    clearTimeout(reloadTimerRef.current as any);
    reloadTimerRef.current = setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.fetch();
      }
      scheduleNextReload();
    }, 1000 * 20);
  };

  const handleRedeploy = async (task: InboxHistory) => {
    Modal.confirm({
      icon: <InfoCircleOutlined />,
      content: `Resend message number #${task.id} to ${task.environment}.`,
      onOk: async () => {
        const res = await csInboxService.redeployTask(task.id ?? 0);
        if (res.ok) {
          message.success(`Message number #${task.id} is scheduled to resend successfully!`);
        }
      },
    });
  };

  const actionColDef: ColDef = {
    headerName: '',
    colId: 'action',
    cellRendererFramework: ({ data }: { data: InboxHistory }) => (
      <Button
        icon={<RollbackOutlined />}
        title="Resend"
        type="primary"
        onClick={() => handleRedeploy(data)}
        disabled={!authorized}
      />
    ),
    minWidth: 60,
    maxWidth: 60,
    pinned: 'right',
  };

  useEffect(() => {
    if (query.get('environment')) {
      setEnvironment(query.get('environment') ?? undefined);
    }
  }, []);

  useEffect(() => {
    scheduleNextReload();
    return () => clearTimeout(reloadTimerRef.current as any);
  }, []);

  useEffect(() => {
    if (pagingOptionsRef.current) {
      pagingOptionsRef.current.environment = environment;
    }
    if (gridRef.current) {
      gridRef.current.fetch();
    }
    updateQuery();
  }, [environment]);

  return (
    <Card
      className="flex flex-col flex-1 min-h-full"
      bordered={false}
      bodyStyle={{ padding: 16, display: 'flex', flexDirection: 'column', flex: '1 1 100%' }}
    >
      <div className="flex items-center pb-4">
        <span className="ml-auto">
          <Space>
            <EnvironmentSelect
              value={pagingOptionsRef.current?.environment as string}
              onChange={setEnvironment}
              allowClear
              style={{ minWidth: 200 }}
            />
            {CsInboxV3Location.toLink(
              <Button type="primary" icon={<CloudUploadOutlined />} disabled={!authorized}>
                Schedule Inbox
              </Button>,
            )}
          </Space>
          <Divider type="vertical" />
          <ReloadOutlined onClick={() => gridRef.current?.fetch()} />
        </span>
      </div>

      <ReactResizeDetector
        handleHeight
        render={({ height }) => (
          <div className="flex-auto ag-theme-alpine bordered" style={{ height }}>
            <PagingAgGrid<InboxHistory>
              request={csInboxService.paging}
              requestParams={{ where: pagingOptionsRef.current }}
              actionRef={gridRef}
              defaultColDef={{
                sortable: true,
                tooltipValueGetter: ({ valueFormatted }) => valueFormatted,
              }}
              columnDefs={[...colDefs, actionColDef]}
              enableBrowserTooltips
              pagination
              paginationPageSize={25}
            />
          </div>
        )}
      />
    </Card>
  );
};

export default InboxHistoryList;
